import React, { createContext, useCallback, useContext, useState } from 'react'

const TOKEN_KEY = 'mimgrc_token'
const USER_KEY = 'mimgrc_user'
const IMPERSONATOR_TOKEN_KEY = 'mimgrc_impersonator_token'
const IMPERSONATOR_USER_KEY = 'mimgrc_impersonator_user'

/** Decodes a JWT and returns true only if it exists and has not expired. */
function isTokenValid(token: string | null): boolean {
  if (!token) return false
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
    // exp is in seconds; Date.now() is in milliseconds — give a 30s grace buffer
    return typeof payload.exp === 'number' && payload.exp * 1000 > Date.now() - 30_000
  } catch {
    return false
  }
}

export type AuthMethod = 'sso' | 'local' | 'unknown'

export function getTokenDiagnostics(token: string | null): { valid: boolean; reason: string } {
  if (!token) return { valid: false, reason: 'Missing token' }
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
    if (typeof payload.exp !== 'number') return { valid: false, reason: 'Token has no exp claim' }
    if (payload.exp * 1000 <= Date.now() - 30_000) return { valid: false, reason: 'Token is expired' }
    return { valid: true, reason: 'Token valid' }
  } catch {
    return { valid: false, reason: 'Token format is invalid' }
  }
}

export interface AuthUser {
  userId: string
  displayName: string
  email?: string
  role?: string
  businessLevel?: string
  functionalRoles?: string[]
  divisionId?: string
  subdivisionId?: string
  businessEntityIds?: string[]
  divisionName?: string
  subdivisionName?: string
  businessEntityNames?: string[]
  isAutoProvisioned?: boolean
  isProfileComplete?: boolean
  authMethod?: AuthMethod
  entraObjectId?: string
  entraTenantId?: string
  entraPreferredUsername?: string
  entraName?: string
  entraIdentityProvider?: string
  isImpersonating?: boolean
  impersonatorUserId?: string
  impersonatorDisplayName?: string
}

interface AuthState {
  token: string | null
  user: AuthUser | null
  isAuthenticated: boolean
}

interface AuthContextValue extends AuthState {
  login: (token: string, user: AuthUser) => void
  updateUser: (user: AuthUser) => void
  startImpersonation: (token: string, user: AuthUser) => void
  stopImpersonation: () => boolean
  logout: () => void
  setToken: (token: string | null) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const token = localStorage.getItem(TOKEN_KEY)
    const userJson = localStorage.getItem(USER_KEY)
    let user: AuthUser | null = null
    if (userJson) {
      try {
        user = JSON.parse(userJson) as AuthUser
        if (localStorage.getItem(IMPERSONATOR_TOKEN_KEY)) {
          user = { ...user, isImpersonating: true }
        }
      } catch {
        localStorage.removeItem(USER_KEY)
      }
    }

    // If the stored token is expired/invalid, clear it immediately rather than
    // letting the user land on a page and get a wall of 401 errors.
    if (!isTokenValid(token)) {
      localStorage.removeItem(TOKEN_KEY)
      localStorage.removeItem(USER_KEY)
      return { token: null, user: null, isAuthenticated: false }
    }

    return {
      token,
      user,
      isAuthenticated: true,
    }
  })

  const login = useCallback((token: string, user: AuthUser) => {
    localStorage.removeItem(IMPERSONATOR_TOKEN_KEY)
    localStorage.removeItem(IMPERSONATOR_USER_KEY)
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(USER_KEY, JSON.stringify(user))
    setState({ token, user, isAuthenticated: true })
  }, [])

  const updateUser = useCallback((user: AuthUser) => {
    localStorage.setItem(USER_KEY, JSON.stringify(user))
    setState((prev) => ({ ...prev, user }))
  }, [])

  const startImpersonation = useCallback((token: string, user: AuthUser) => {
    const currentToken = localStorage.getItem(TOKEN_KEY)
    const currentUser = localStorage.getItem(USER_KEY)
    if (currentToken && currentUser && !localStorage.getItem(IMPERSONATOR_TOKEN_KEY)) {
      localStorage.setItem(IMPERSONATOR_TOKEN_KEY, currentToken)
      localStorage.setItem(IMPERSONATOR_USER_KEY, currentUser)
    }
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(USER_KEY, JSON.stringify({ ...user, isImpersonating: true }))
    setState({ token, user: { ...user, isImpersonating: true }, isAuthenticated: true })
  }, [])

  const stopImpersonation = useCallback((): boolean => {
    const impersonatorToken = localStorage.getItem(IMPERSONATOR_TOKEN_KEY)
    const impersonatorUserJson = localStorage.getItem(IMPERSONATOR_USER_KEY)
    if (!impersonatorToken || !impersonatorUserJson) return false

    let impersonatorUser: AuthUser
    try {
      impersonatorUser = JSON.parse(impersonatorUserJson) as AuthUser
    } catch {
      localStorage.removeItem(IMPERSONATOR_TOKEN_KEY)
      localStorage.removeItem(IMPERSONATOR_USER_KEY)
      return false
    }

    localStorage.setItem(TOKEN_KEY, impersonatorToken)
    localStorage.setItem(USER_KEY, impersonatorUserJson)
    localStorage.removeItem(IMPERSONATOR_TOKEN_KEY)
    localStorage.removeItem(IMPERSONATOR_USER_KEY)
    setState({ token: impersonatorToken, user: impersonatorUser, isAuthenticated: true })
    return true
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
    localStorage.removeItem(IMPERSONATOR_TOKEN_KEY)
    localStorage.removeItem(IMPERSONATOR_USER_KEY)
    setState({ token: null, user: null, isAuthenticated: false })
  }, [])

  const setToken = useCallback((token: string | null) => {
    setState((prev) => ({ ...prev, token, isAuthenticated: !!token }))
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  }, [])

  return (
    <AuthContext.Provider value={{ ...state, login, updateUser, startImpersonation, stopImpersonation, logout, setToken }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
